import { useState, ChangeEvent, FormEvent, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { WORKFLOW_CREATE_METHOD_OPTIONS } from "@constants/workflow-create-method-options";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { WorkflowCreateMethodTypes } from "@enums/workflow-create-method-types";
import { formatAppliedProfiles } from "@formatters/applied-profiles/applied-profiles-formatter";
import { formatWorkflowTemplateDetailsTemplatePartial } from "@formatters/workflow-template-details/workflow-template-details-template-formatter";
import { SelectOption } from "@idw-types/common";
import { AxnAvailablePropertyCategory, AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { WorkflowRedirectURL, WorkflowTemplate } from "@idw-types/data/reference-data/generic";
import { FormattedWorkflowConfiguration } from "@idw-types/data/workflow-configuration";
import { AdvancedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { WorkflowTemplateDetails } from "@idw-types/data/workflow-template-details";
import { IDWSession } from "@idw-types/data/idw-session";
import { EntityImportValidationError } from "@idw-types/data/entity-import-validation-error";
import { createWorkflowReduxRequest } from "@redux-requests/workflows";
import { addObjectsToArrayInCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { validateVerificationWorkflowRequest } from "@requests/service-configuration";
import { getWorkflowDetailsAxnRequest } from "@requests/service-configuration";
import { getWorkflowTemplateDetailsRequest } from "@requests/management-console/get-workflow-template-details";
import { extractAvailablePropertiesFromCategories } from "@utils/available-properties-util";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import {
    getCombinedAppliedProfileRedirectURLs,
    getRpServiceConfigurationPropertyRedirectURLs,
    extractRedirectURLsFromWorkflowTemplatePartial
} from "@utils/redirect-urls/redirect-urls-util";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { LoadingModal } from "@containers/common/modals/loading-modal/loading-modal";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { CopyWorkflowSection } from "./copy-workflow-section/copy-workflow-section";
import { CreateWorkflowFromTemplateSection } from "./create-workflow-from-template-section/create-workflow-from-template-section";
import { ImportWorkflowSection } from "./import-workflow-section/import-workflow-section";
import defaultStyles from "@containers/common/modals/default-modal/default-modal.less";

type AddVerificationWorkflowModalProps = {
    onModalClose: () => void;
    session: IDWSession;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    allAxnCompanies: Array<AxnCompany>;
    allAxnRelyingParties: Array<AxnRelyingParty>;
    allowedAxnCompanies: Array<AxnCompany>;
    allowedAxnRelyingParties: Array<AxnRelyingParty>;
    workflowTemplates: Array<WorkflowTemplate>;
    workflowRedirectURLs: Array<WorkflowRedirectURL>;
    workflowConfigurations: Array<FormattedWorkflowConfiguration>;
};

type AddVerificationWorkflowModalState = {
    selectedCreateMethod: SelectOption<WorkflowCreateMethodTypes>;
    selectedWorkflowTemplateOption: SelectOption;
    selectedWorkflowTemplate: WorkflowTemplateDetails;
    selectedFileName: string;
    selectedFile: File;
    selectedWorkflowConfigurationOption: SelectOption;
    selectedWorkflowConfiguration: AdvancedWorkflowDetailsConfiguration;
    //if create from template - then these are used
    selectedWorkflowOrganizationOption: SelectOption;
    selectedWorkflowRelyingPartyOption: SelectOption;
    availableRelyingParties: Array<AxnRelyingParty>;
    workflowName: string;
    workflowRedirectUrls: Array<string>;
};

type AddVerificationWorkflowModalValidationState = {
    selectedFileJSON: any;
    importErrors: { [key: string]: Array<EntityImportValidationError> };
    validationComplete: boolean;
};

export const AddVerificationWorkflowModal = (props: AddVerificationWorkflowModalProps) => {
    const dispatch = useDispatch();
    const modalContext = useModalContext();

    const {
        onModalClose,
        session,
        axnAvailablePropertyCategories,
        allAxnCompanies,
        allAxnRelyingParties,
        allowedAxnCompanies,
        allowedAxnRelyingParties,
        workflowRedirectURLs,
        workflowTemplates,
        workflowConfigurations
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const allAxnRelyingPartiesRef = useRef(allAxnRelyingParties);
    const allowedAxnRelyingPartiesRef = useRef(allowedAxnRelyingParties);

    const getInitialWorkflowRedirectURLs = () => {
        return workflowRedirectURLs ? workflowRedirectURLs.map(url => url.redirectURL) : []
    };

    const availableServiceCreationMethodOptions = useMemo(() => {
        const finalList: Array<SelectOption<WorkflowCreateMethodTypes>> = [];
        if (session.userPermissions.canCreateWorkflowFromTemplate) {
            finalList.push(WORKFLOW_CREATE_METHOD_OPTIONS[0]);
        }
        if (session.userPermissions.canCreateWorkflowFromImportFile) {
            finalList.push(WORKFLOW_CREATE_METHOD_OPTIONS[1]);
        }
        if (session.userPermissions.canCreateWorkflowFromExisting) {
            finalList.push(WORKFLOW_CREATE_METHOD_OPTIONS[2]);
        }
        return finalList;
    }, [session.userPermissions]);


    const [isLoadingRequiredData, setIsLoadingRequiredData] = useState(false);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [state, setState] = useState<AddVerificationWorkflowModalState>({
        selectedCreateMethod: availableServiceCreationMethodOptions[0] || null,
        selectedWorkflowTemplateOption: null,
        selectedWorkflowTemplate: null,
        selectedFileName: "",
        selectedFile: null,
        selectedWorkflowConfigurationOption: null,
        selectedWorkflowConfiguration: null,
        //if create from template - then these are used
        selectedWorkflowOrganizationOption: allowedAxnCompanies.length === 1 ? mapEntityWithNameAndIdToSelectOption(allowedAxnCompanies[0]) : null,
        selectedWorkflowRelyingPartyOption: null,
        availableRelyingParties: allowedAxnRelyingPartiesRef.current,
        workflowName: "",
        workflowRedirectUrls: getInitialWorkflowRedirectURLs()
    });
    const [validationState, setValidationState] = useState<AddVerificationWorkflowModalValidationState>({
        selectedFileJSON: null,
        importErrors: {},
        validationComplete: false
    });

    const canAddRelyingPartyDueToLimits = useMemo(() => {
        return session.limits.relyingParties > allAxnRelyingParties.length;
    }, [session.limits.relyingParties, allAxnRelyingParties.length]);


    const availableProperties = useMemo(() => {
        return extractAvailablePropertiesFromCategories(axnAvailablePropertyCategories);
    }, [axnAvailablePropertyCategories]);

    const onAddRelyingPartySuccess = (newAxnRelyingParty: AxnRelyingParty) => {
        dispatch(addObjectsToArrayInCommonReferenceData([newAxnRelyingParty], "axnRelyingParties"));

        let selectedWorkflowOrganizationOption: SelectOption = null;
        if (newAxnRelyingParty.companyID) {
            const company = allowedAxnCompanies.find(company => company.id === newAxnRelyingParty.companyID);
            if (company) {
                selectedWorkflowOrganizationOption = mapEntityWithNameAndIdToSelectOption(company);
            }
        }

        allAxnRelyingPartiesRef.current = [...allAxnRelyingPartiesRef.current, newAxnRelyingParty];
        allowedAxnRelyingPartiesRef.current = [...allowedAxnRelyingPartiesRef.current, newAxnRelyingParty];

        const availableRelyingParties = allowedAxnRelyingPartiesRef.current.filter((rp: any) => rp.companyID === newAxnRelyingParty.companyID);

        setState(prevState => ({
            ...prevState,
            availableRelyingParties: availableRelyingParties,
            selectedWorkflowOrganizationOption,
            selectedWorkflowRelyingPartyOption: { label: newAxnRelyingParty.name, value: newAxnRelyingParty.id }
        }));
    };

    const onClickAddRedirectUrl = () => {
        setState({
            ...state,
            workflowRedirectUrls: [...state.workflowRedirectUrls, ""]
        });
    };

    const removeWorkflowRedirectUrl = (index: number) => {
        const newWorkflowRedirectUrls = [...state.workflowRedirectUrls];
        newWorkflowRedirectUrls.splice(index, 1);
        setState({
            ...state,
            workflowRedirectUrls: newWorkflowRedirectUrls
        });
    };

    const renderAlmostDoneModal = () => {
        const renderModal = (onModalClose: () => void) => {
            return (
                <LoadingModal
                    title={resources.modals.addVerificationWorkflow.loadingModal.title}
                    message={resources.modals.addVerificationWorkflow.loadingModal.message}
                    buttonText={resources.modals.addVerificationWorkflow.loadingModal.buttonText}
                    onModalClose={onModalClose}
                />
            );
        }

        modalContext.setModalState({
            modalType: ModalTypes.Loading,
            renderFunction: renderModal
        });
    };

    const dismountModal = () => {
        modalContext.setModalState({
            modalType: undefined,
            renderFunction: undefined
        });
    };

    const handleUseTemplateSubmit = async () => {
        return await createWorkflowReduxRequest(dispatch)(
            state.workflowName,
            undefined,
            state.selectedWorkflowTemplateOption?.value,
            undefined,
            state.selectedWorkflowRelyingPartyOption?.value,
            state.workflowRedirectUrls,
            allAxnCompanies,
            allAxnRelyingPartiesRef.current
        );
    };

    const handleUseImportSubmit = async () => {
        return await createWorkflowReduxRequest(dispatch)(
            state.workflowName,
            undefined,
            undefined,
            validationState.selectedFileJSON,
            state.selectedWorkflowRelyingPartyOption?.value,
            state.workflowRedirectUrls,
            allAxnCompanies,
            allAxnRelyingPartiesRef.current
        );
    };

    const handleUseValidateSubmit = async () => {
        const { data, error } = await validateVerificationWorkflowRequest(
            validationState.selectedFileJSON
        );
        if (data && Object.keys(data).length > 0) {
            setValidationState({ ...validationState, importErrors: data, validationComplete: true });
            setRequestInProgress(false);
        } else if (data && Object.keys(data).length === 0) {
            const workflowName = validationState.selectedFileJSON.workflow?.name || "";
            const jsonCompanyID = `${validationState.selectedFileJSON.workflow?.companyID || ""}`;
            const organization = jsonCompanyID ? allowedAxnCompanies.find(company => jsonCompanyID === company.id) : null;
            const jsonRelyingPartyID = `${validationState.selectedFileJSON.workflow?.relyingPartyID || ""}`;
            const relyingParty = jsonRelyingPartyID ? allowedAxnCompanies.find(rp => jsonRelyingPartyID === rp.id) : null;

            setValidationState({ ...validationState, importErrors: {}, validationComplete: true });
            setState({
                ...state,
                workflowName: workflowName,
                selectedWorkflowOrganizationOption: organization ? mapEntityWithNameAndIdToSelectOption(organization) : null,
                selectedWorkflowRelyingPartyOption: relyingParty ? mapEntityWithNameAndIdToSelectOption(relyingParty) : null
            });
            setRequestInProgress(false);
        } else {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.validateVerificationWorkflow.error.title,
                resources.requests.validateVerificationWorkflow.error.message
            );
            setRequestInProgress(false);
        }
    };

    const handleUseCopySubmit = async () => {
        return await createWorkflowReduxRequest(dispatch)(
            state.workflowName,
            state.selectedWorkflowConfigurationOption?.value,
            undefined,
            undefined,
            state.selectedWorkflowRelyingPartyOption?.value,
            state.workflowRedirectUrls,
            allAxnCompanies,
            allAxnRelyingPartiesRef.current
        );
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);

        let timeout: NodeJS.Timeout;

        let createWorkflowResponse;
        switch (state.selectedCreateMethod.value) {
            case WorkflowCreateMethodTypes.USE_TEMPLATE:
                timeout = setTimeout(() => { renderAlmostDoneModal() }, 10000);
                createWorkflowResponse = await handleUseTemplateSubmit();
                break;
            case WorkflowCreateMethodTypes.USE_IMPORT:
                if (validationState.validationComplete) {
                    timeout = setTimeout(() => { renderAlmostDoneModal() }, 10000);
                    createWorkflowResponse = await handleUseImportSubmit();
                } else {
                    await handleUseValidateSubmit();
                }
                break;
            case WorkflowCreateMethodTypes.USE_COPY:
                timeout = setTimeout(() => { renderAlmostDoneModal() }, 10000);
                createWorkflowResponse = await handleUseCopySubmit();
                break;
        }

        if (timeout) {
            clearTimeout(timeout);
        }
        if (createWorkflowResponse) {
            dismountModal();
            onModalClose();
        } else {
            setRequestInProgress(false);
        }
    };

    const handleUpdateIWantTo = (newValue: SelectOption<WorkflowCreateMethodTypes>) => {
        if (newValue) {
            setState({
                ...state,
                selectedFileName: "",
                selectedFile: null,
                selectedCreateMethod: newValue,
                workflowName: "",
                workflowRedirectUrls: getInitialWorkflowRedirectURLs(),
                selectedWorkflowOrganizationOption: allowedAxnCompanies.length === 1 ? mapEntityWithNameAndIdToSelectOption(allowedAxnCompanies[0]) : null,
                selectedWorkflowRelyingPartyOption: null,
                selectedWorkflowTemplate: null,
                selectedWorkflowTemplateOption: null,
                selectedWorkflowConfiguration: null,
                selectedWorkflowConfigurationOption: null
            });
            setValidationState({ selectedFileJSON: null, importErrors: {}, validationComplete: false });
        }
    };

    const handleUpdateWorkflowTemplate = async (newValue: SelectOption) => {
        if (newValue) {
            setIsLoadingRequiredData(true);
            const { data, error } = await getWorkflowTemplateDetailsRequest(newValue.value);

            const combinedRedirectURLs: Array<string> = getInitialWorkflowRedirectURLs();
            if (data && data.template) {
                const formattedWorkflowTemplate = formatWorkflowTemplateDetailsTemplatePartial(
                    data.template,
                    allAxnRelyingPartiesRef.current,
                    allAxnCompanies,
                    axnAvailablePropertyCategories
                );
                const templateRedirectURLs = extractRedirectURLsFromWorkflowTemplatePartial(formattedWorkflowTemplate);
                combinedRedirectURLs.push(...templateRedirectURLs.map(redirectUrl => redirectUrl.url));
            } else {
                if (error) {
                    logAndDisplayErrorModal(
                        dispatch,
                        error,
                        resources.requests.getWorkflowTemplate.error.title,
                        resources.requests.getWorkflowTemplate.error.message
                    );
                }
            }

            const filteredCombinedRedirectURLs = combinedRedirectURLs.filter((item, index) => {
                return combinedRedirectURLs.indexOf(item) === index;
            });

            setState({
                ...state,
                selectedWorkflowTemplateOption: newValue,
                selectedWorkflowTemplate: data || null,
                workflowRedirectUrls: filteredCombinedRedirectURLs
            });

            setIsLoadingRequiredData(false);
        }
    };

    const handleUpdateFile = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files.length > 0) {
            setState({ ...state, selectedFileName: event.target.value || "", selectedFile: event.target.files[0] });

            try {
                const fileReader = new FileReader();
                fileReader.onload = function () {
                    const jsonVerificationWorkflow = JSON.parse(fileReader.result as string);
                    setValidationState({ selectedFileJSON: jsonVerificationWorkflow, importErrors: {}, validationComplete: false });
                };
                fileReader.readAsText(event.target.files[0]);
            } catch (error) {
                setState({ ...state, selectedFileName: "", selectedFile: null });
                setValidationState({ selectedFileJSON: null, importErrors: {}, validationComplete: false });
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.modals.importVerificationWorkflowConfiguration.loadFile.error.title,
                    resources.modals.importVerificationWorkflowConfiguration.loadFile.error.message
                );
            }
        } else {
            setState({ ...state, selectedFileName: "", selectedFile: null });
            setValidationState({ selectedFileJSON: null, importErrors: {}, validationComplete: false });
        }
    };

    const handleUpdateVerificationWorkflow = async (newValue: SelectOption) => {
        if (newValue) {
            setIsLoadingRequiredData(true);
            const workflowDetailsResponse = await getWorkflowDetailsAxnRequest(newValue.value);

            const workflowRedirectURLs: Array<string> = [];
            const primaryRpServiceRedirectURLs: Array<string> = [];
            if (workflowDetailsResponse.data) {
                // get all of the workflow profile property redirect URLs
                const formattedAppliedProfiles = formatAppliedProfiles(
                    workflowDetailsResponse.data.workflow.appliedProfiles,
                    allAxnCompanies,
                    availableProperties,
                    false
                );
                const appliedProfileRedirectURLs = getCombinedAppliedProfileRedirectURLs(formattedAppliedProfiles, false);
                if (appliedProfileRedirectURLs.length > 0) {
                    workflowRedirectURLs.push(...appliedProfileRedirectURLs.map(redirectUrl => redirectUrl.url));
                }

                // get the primary service data
                if (workflowDetailsResponse.data.workflow.rpServiceWorkflows.length > 0) {
                    const primaryService = workflowDetailsResponse.data.workflow.rpServiceWorkflows.find(rpsw => isStringTrue(rpsw.primaryServiceFlag));
                    if (primaryService) {
                        // get all of the service level redirect URLs
                        const serviceDetails = workflowDetailsResponse.data.rpServices.find(service => service.id === primaryService.rpAXNServiceID);
                        if (serviceDetails) {
                            const servicePropertyRedirects = getRpServiceConfigurationPropertyRedirectURLs(
                                serviceDetails.properties,
                                serviceDetails.id,
                                false
                            ).map(rURL => rURL.url);

                            const serviceRedirectURLs = serviceDetails.redirects.map(redirect => redirect.redirectURI);
                            primaryRpServiceRedirectURLs.push(...servicePropertyRedirects, ...serviceRedirectURLs);
                        }
                    }
                }

                const combinedRedirectURLs: Array<string> = [
                    ...getInitialWorkflowRedirectURLs(),
                    ...workflowRedirectURLs,
                    ...primaryRpServiceRedirectURLs
                ];

                const filteredCombinedRedirectURLs = combinedRedirectURLs.filter((item, index) => {
                    return combinedRedirectURLs.indexOf(item) === index;
                });

                setState({
                    ...state,
                    selectedWorkflowConfigurationOption: newValue,
                    selectedWorkflowConfiguration: workflowDetailsResponse.data,
                    workflowRedirectUrls: filteredCombinedRedirectURLs
                });
            }

            setIsLoadingRequiredData(false);
        }
    };

    const handleUpdateWorkflowName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            workflowName: event.target.value
        });
    };

    const handleUpdateWorkflowOrganization = (newValue: SelectOption) => {
        if (newValue) {
            const availableRelyingParties = allAxnRelyingPartiesRef.current.filter((rp: any) => rp.companyID === newValue.value);
            if (state.selectedWorkflowRelyingPartyOption && availableRelyingParties.some(rp => rp.id === state.selectedWorkflowRelyingPartyOption.value)) {
                setState({
                    ...state,
                    selectedWorkflowOrganizationOption: newValue,
                    availableRelyingParties: availableRelyingParties
                })
            } else {
                setState({
                    ...state,
                    selectedWorkflowOrganizationOption: newValue,
                    availableRelyingParties: availableRelyingParties,
                    selectedWorkflowRelyingPartyOption: null
                })
            }
        } else {
            setState({
                ...state,
                selectedWorkflowOrganizationOption: null,
                availableRelyingParties: allowedAxnRelyingPartiesRef.current
            });
        }
    };

    const handleUpdateWorkflowRelyingParty = (newValue: SelectOption) => {
        if (newValue) {
            let selectedWorkflowOrganizationOption: SelectOption = null;
            let availableRelyingParties = allowedAxnRelyingPartiesRef.current;
            const relyingParty = allowedAxnRelyingPartiesRef.current.find(rp => rp.id === newValue.value);
            if (relyingParty) {
                const company = allowedAxnCompanies.find(company => company.id === relyingParty.companyID);
                if (company) {
                    availableRelyingParties = allowedAxnRelyingPartiesRef.current.filter((rp: any) => rp.companyID === company.id);
                    selectedWorkflowOrganizationOption = mapEntityWithNameAndIdToSelectOption(company);
                }
            }

            setState({
                ...state,
                availableRelyingParties: availableRelyingParties,
                selectedWorkflowRelyingPartyOption: newValue,
                selectedWorkflowOrganizationOption: selectedWorkflowOrganizationOption
            });
        }
    };

    const handleUpdateWorkflowRedirectUrl = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        const newWorkflowRedirectUrls = state.workflowRedirectUrls.map((url, i) => index === i ? event.target.value : url);
        setState({
            ...state,
            workflowRedirectUrls: newWorkflowRedirectUrls
        });
    };

    const buttonLabel = useMemo(() => {
        switch (state.selectedCreateMethod.value) {
            case WorkflowCreateMethodTypes.USE_TEMPLATE:
                return resources.modals.addVerificationWorkflow.buttons.deployNow.label;
            case WorkflowCreateMethodTypes.USE_COPY:
                return resources.modals.addVerificationWorkflow.buttons.deployNow.label;
            case WorkflowCreateMethodTypes.USE_IMPORT:
                if (validationState.validationComplete) {
                    return resources.modals.addVerificationWorkflow.buttons.deployNow.label;
                } else {
                    return resources.modals.addVerificationWorkflow.buttons.validate.label;
                }
            default:
                return resources.common.buttons.save.label;
        }
    }, [
        state.selectedCreateMethod,
        validationState.validationComplete
    ]);

    return (
        <DefaultModal
            automationId="add-verification-workflow-modal"
            ariaLabelledBy="add-verification-workflow-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-verification-workflow-title"} closeButton>
                    <Modal.Title>{resources.modals.addVerificationWorkflow.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span className={defaultStyles.description}>
                        {resources.modals.addVerificationWorkflow.description}
                    </span>
                    <SelectFormGroup
                        controlId={`verification-workflow-iwantto-select`}
                        label={resources.modals.addVerificationWorkflow.inputs.iWantTo.label}
                        ariaLabel={resources.modals.addVerificationWorkflow.inputs.iWantTo.label}
                        automationId={`verification-workflow-iwantto-select`}
                        onChange={handleUpdateIWantTo}
                        options={availableServiceCreationMethodOptions}
                        selectedOption={state.selectedCreateMethod}
                        disabled={requestInProgress}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                    {state.selectedCreateMethod.value === WorkflowCreateMethodTypes.USE_IMPORT && (
                        <ImportWorkflowSection
                            importErrors={validationState.importErrors}
                            selectedFileJSON={validationState.selectedFileJSON}
                            validationComplete={validationState.validationComplete}
                            labelWidth={labelWidth}
                            canAddNewRelyingParty={session.userPermissions.canCreateRelyingParties}
                            canAddRelyingPartyDueToLimits={canAddRelyingPartyDueToLimits}
                            requestInProgress={requestInProgress}
                            axnCompanies={allowedAxnCompanies}
                            availableRelyingParties={state.availableRelyingParties}
                            workflowName={state.workflowName}
                            selectedFileName={state.selectedFileName}
                            workflowRedirectUrls={state.workflowRedirectUrls}
                            selectedWorkflowOrganizationOption={state.selectedWorkflowOrganizationOption}
                            selectedWorkflowRelyingPartyOption={state.selectedWorkflowRelyingPartyOption}
                            handleUpdateFile={handleUpdateFile}
                            onAddRelyingPartySuccess={onAddRelyingPartySuccess}
                            handleUpdateWorkflowName={handleUpdateWorkflowName}
                            handleUpdateWorkflowOrganization={handleUpdateWorkflowOrganization}
                            handleUpdateWorkflowRelyingParty={handleUpdateWorkflowRelyingParty}
                            handleUpdateWorkflowRedirectUrl={handleUpdateWorkflowRedirectUrl}
                            onClickAddRedirectUrl={onClickAddRedirectUrl}
                            removeWorkflowRedirectUrl={removeWorkflowRedirectUrl}
                        />
                    )}
                    {state.selectedCreateMethod.value === WorkflowCreateMethodTypes.USE_COPY && (
                        <CopyWorkflowSection
                            labelWidth={labelWidth}
                            canAddNewRelyingParty={session.userPermissions.canCreateRelyingParties}
                            canAddRelyingPartyDueToLimits={canAddRelyingPartyDueToLimits}
                            requestInProgress={requestInProgress}
                            isLoadingRequiredData={isLoadingRequiredData}
                            axnCompanies={allowedAxnCompanies}
                            relyingParties={allowedAxnRelyingPartiesRef.current}
                            availableRelyingParties={state.availableRelyingParties}
                            workflowConfigurations={workflowConfigurations}
                            workflowName={state.workflowName}
                            workflowRedirectUrls={state.workflowRedirectUrls}
                            selectedWorkflowOrganizationOption={state.selectedWorkflowOrganizationOption}
                            selectedWorkflowRelyingPartyOption={state.selectedWorkflowRelyingPartyOption}
                            selectedWorkflowConfigurationOption={state.selectedWorkflowConfigurationOption}
                            handleUpdateVerificationWorkflow={handleUpdateVerificationWorkflow}
                            onAddRelyingPartySuccess={onAddRelyingPartySuccess}
                            handleUpdateWorkflowName={handleUpdateWorkflowName}
                            handleUpdateWorkflowOrganization={handleUpdateWorkflowOrganization}
                            handleUpdateWorkflowRelyingParty={handleUpdateWorkflowRelyingParty}
                            handleUpdateWorkflowRedirectUrl={handleUpdateWorkflowRedirectUrl}
                            onClickAddRedirectUrl={onClickAddRedirectUrl}
                            removeWorkflowRedirectUrl={removeWorkflowRedirectUrl}
                        />
                    )}
                    {state.selectedCreateMethod.value === WorkflowCreateMethodTypes.USE_TEMPLATE && (
                        <CreateWorkflowFromTemplateSection
                            labelWidth={labelWidth}
                            canAddNewRelyingParty={session.userPermissions.canCreateRelyingParties}
                            canAddRelyingPartyDueToLimits={canAddRelyingPartyDueToLimits}
                            requestInProgress={requestInProgress}
                            isLoadingRequiredData={isLoadingRequiredData}
                            axnCompanies={allowedAxnCompanies}
                            availableRelyingParties={state.availableRelyingParties}
                            workflowTemplates={workflowTemplates}
                            workflowName={state.workflowName}
                            workflowRedirectUrls={state.workflowRedirectUrls}
                            selectedWorkflowOrganizationOption={state.selectedWorkflowOrganizationOption}
                            selectedWorkflowRelyingPartyOption={state.selectedWorkflowRelyingPartyOption}
                            selectedWorkflowTemplateOption={state.selectedWorkflowTemplateOption}
                            handleUpdateWorkflowTemplate={handleUpdateWorkflowTemplate}
                            onAddRelyingPartySuccess={onAddRelyingPartySuccess}
                            handleUpdateWorkflowName={handleUpdateWorkflowName}
                            handleUpdateWorkflowOrganization={handleUpdateWorkflowOrganization}
                            handleUpdateWorkflowRelyingParty={handleUpdateWorkflowRelyingParty}
                            handleUpdateWorkflowRedirectUrl={handleUpdateWorkflowRedirectUrl}
                            onClickAddRedirectUrl={onClickAddRedirectUrl}
                            removeWorkflowRedirectUrl={removeWorkflowRedirectUrl}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="verification-workflow-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="verification-workflow-submit-button"
                        disabled={requestInProgress || (validationState.validationComplete && Object.keys(validationState.importErrors).length > 0)}
                        hideSpinner={validationState.validationComplete && Object.keys(validationState.importErrors).length > 0}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={buttonLabel}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal >
    );
};
